import { Link, NavLink } from "react-router-dom";
import { Sun, PlusCircle, LayoutDashboard, MapPin, Activity } from "lucide-react";

import { useAccount } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";

import { AccountMenu } from "./AccountMenu";
import { AnonymousMenu } from "./AnonymousMenu";

// S16-1 — `min-h-[44px]` : cible tactile confortable au pouce sur mobile
// (recommandation WCAG 2.5.5 ≥ 44 px) pour les contrôles de la barre minimale.
const navItem =
  "inline-flex min-h-[44px] items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900";
const navItemActive = "text-slate-900 bg-slate-100";

/**
 * Header applicatif.
 *
 * **Barre** : logo (→ `/` landing) + raccourcis-icônes vers « Tableau de bord »
 * (`/app`) et « Mes sites » (`/sites`) + action primaire « Nouvelle simulation »
 * (`/simulations/new`) + l'avatar/contrôles d'auth (6171e19). « Profil » /
 * « Paramètres » vivent dans le menu déroulant (cf. `AccountMenu` /
 * `AnonymousMenu`) — pas de doublon barre ↔ dropdown pour ces deux-là.
 *
 * L'a11y du menu déroulant (clavier, `role=menu`, focus, clic extérieur, Échap)
 * est portée par `DropdownMenu`.
 */
function AuthControls(): JSX.Element | null {
  const { data: account, isLoading } = useAccount();

  // Évite tout flash de menu avant de connaître l'état réel.
  if (isLoading) {
    return null;
  }

  // S16-2 — même structure connecté / anonyme : un menu déroulant dans les
  // deux cas (le menu anonyme expose Tableau de bord + Se connecter + Créer
  // un compte).
  return account ? (
    <AccountMenu email={account.email} isAdmin={account.is_admin} />
  ) : (
    <AnonymousMenu />
  );
}

/**
 * Raccourci-icône « Surveillance plateforme » (`/admin`), visible uniquement
 * pour le compte admin. Masquage **cosmétique** aligné sur l'entrée du
 * dropdown (`AccountMenu`) : l'API garde réellement la page (403 sinon).
 */
function AdminLink(): JSX.Element | null {
  const { data: account } = useAccount();
  if (account?.is_admin !== true) {
    return null;
  }
  return (
    <NavLink
      to="/admin"
      className={({ isActive }) => cn(navItem, isActive && navItemActive)}
      title="Surveillance plateforme"
    >
      <Activity className="h-4 w-4" aria-hidden />
      <span className="sr-only">Surveillance plateforme</span>
    </NavLink>
  );
}

export function Navbar(): JSX.Element {
  return (
    <header className="border-b border-slate-200 bg-white">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-2 px-4 py-3">
        {/* 2026-06-13 (décision PO) : le logo pointe vers la landing `/`. */}
        <Link
          to="/"
          className="flex items-center gap-2 text-lg font-semibold"
        >
          <Sun className="h-5 w-5 text-amber-500" aria-hidden />
          <span>SunMatch</span>
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900">
            bêta
          </span>
        </Link>

        {/* S16-2 — barre minimale : action primaire + avatar uniquement. */}
        <nav
          className="flex items-center gap-1"
          aria-label="Navigation principale"
        >
          <NavLink
            to="/app"
            className={({ isActive }) => cn(navItem, isActive && navItemActive)}
            title="Tableau de bord"
          >
            <LayoutDashboard className="h-4 w-4" aria-hidden />
            <span className="sr-only">Tableau de bord</span>
          </NavLink>
          <NavLink
            to="/sites"
            className={({ isActive }) => cn(navItem, isActive && navItemActive)}
            title="Mes sites"
          >
            <MapPin className="h-4 w-4" aria-hidden />
            <span className="sr-only">Mes sites</span>
          </NavLink>
          <NavLink
            to="/simulations/new"
            className={({ isActive }) => cn(navItem, isActive && navItemActive)}
            title="Nouvelle simulation"
          >
            <PlusCircle className="h-4 w-4" aria-hidden />
            {/* Barre icône-seule (cohérent avec « Tableau de bord » / « Mes
                sites ») : le libellé texte est retiré, l'icône + le `sr-only`
                (+ `title`) assurent le sens et le nom accessible. */}
            <span className="sr-only">Nouvelle simulation</span>
          </NavLink>
          <AdminLink />
          <AuthControls />
        </nav>
      </div>
    </header>
  );
}
